import React from "react";
import { Box, Typography } from "@mui/material";
import InformationTable from "./content-blocks/InformationTable";
import ExperimentTable from "./ExperimentTable";
import HTMLContent from "./content-blocks/HTMLContent";
import Image from "./content-blocks/Image";
import ValidatedInput from "./content-blocks/ValidatedInput";
import ValidatedMultipleChoice from "./content-blocks/ValidatedMultipleChoice";
import ValidatedImageSelection from "./content-blocks/ValidatedImageSelection";
import MagnifiedImage from "./content-blocks/MagnifiedImage";
import { normalizedHeading } from '../utils/labNavigation';

const styles = {
  flexColumnStart: {
    display: "flex",
    flexDirection: "column",
    alignItems: "left",
    justifyContent: "flex-start",
  },
  flexColumnCenter: {
    display: "flex",
    flexDirection: "column",
    alignItems: "left",
    justifyContent: "center",
  },
  flexRowWrap: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: "1rem",
    justifyContent: "flex-start",
  },
  flexLeft: {
    display: "flex",
    justifyContent: "left",
    gap: 2,
  },
  flexCenter: {
    display: "flex",
    justifyContent: "center",
    pb: 2,
  },
};

const renderHeader = (contentBlock, headingLevel) => (
  <Typography variant={headingLevel} component={headingLevel} sx={{ mt: 3, mb: 1.5 }}>
    {contentBlock.text}
  </Typography>
);

const renderText = (contentBlock) => (
  <Box
    sx={{
      ...styles.flexLeft,
      pb: 2,
      maxWidth: '65ch',
      justifyContent: contentBlock.centered ? "center" : "left",
    }}
  >
    <HTMLContent content={contentBlock.text} />
  </Box>
);

const renderImage = (contentBlock) => (
  <Box
    sx={{
      ...styles.flexLeft,
      justifyContent: contentBlock.centered ? "center" : "left",
    }}
  >
    <Image
      imageFileName={contentBlock.imageFileName}
      imageAlt={contentBlock.imageAlt}
    />
  </Box>
);

const renderImages = (contentBlock) => (
  <Box
    sx={{
      ...styles.flexRowWrap,
      justifyContent: contentBlock.centered ? "center" : "flex-start",
    }}
  >
    {contentBlock.images.map((image) => (
      <Image
        key={image.imageFileName}
        imageFileName={image.imageFileName}
        imageAlt={image.imageAlt}
        multiImage={true}
        imageTitle={image.imageTitle}
        landscape={image.landscape}
      />
    ))}
  </Box>
);

const renderInformationTable = (contentBlock, label) => (
  <Box sx={styles.flexColumnStart}>
    <InformationTable
      label={label}
      headers={contentBlock.headers}
      rows={contentBlock.rows}
      annotation={contentBlock.annotation}
    />
  </Box>
);

const renderExperimentTable = (contentBlock, label) => (
  <Box sx={styles.flexColumnCenter}>
    <ExperimentTable label={label} headers={contentBlock.headers} rows={contentBlock.rows} />
  </Box>
);

const renderInput = (contentBlock, questionKey) => {
  // Associate the question prompt with the answer field so a screen reader
  // announces which question the field belongs to (WCAG 1.3.1 / 3.3.2).
  const directionsId =
    contentBlock.directions && questionKey ? `${questionKey}-directions` : undefined;
  return (
    <Box
      className="worksheet-question"
      sx={{
        ...styles.flexColumnStart,
        mt: contentBlock.groupedItem ? 2 : 3,
        pt: 2,
        borderTop: 1,
        borderColor: 'divider',
        gap: 2,
      }}
    >
      {contentBlock.directions && (
        <Box id={directionsId}>
          <HTMLContent variant="body1" content={contentBlock.directions} />
        </Box>
      )}
      <ValidatedInput
        questionKey={questionKey}
        equation={contentBlock.equation}
        answerLength={contentBlock.answerLength}
        field={contentBlock.label}
        unitOfMeasure={contentBlock.unitOfMeasure}
        validAnswers={contentBlock.validAnswers}
        tip={contentBlock.tip}
        section={contentBlock._section}
        directionsId={directionsId}
      />
    </Box>
  );
};

const renderMagnifiedImage = (contentBlock) => (
  <Box>
    <MagnifiedImage
      imageFileName={contentBlock.imageFileName}
      imageZoomFileName={contentBlock.imageZoomFileName}
      imageAlt={contentBlock.imageAlt}
    />
  </Box>
);

const renderImageSelection = (contentBlock, questionKey) => (
  <Box sx={styles.flexLeft}>
    <ValidatedImageSelection
      questionKey={questionKey}
      choices={contentBlock.choices}
      directions={contentBlock.directions}
      tip={contentBlock.tip}
      section={contentBlock._section}
    />
  </Box>
);

const renderMultipleChoice = (contentBlock, questionKey) => (
  <Box sx={{ ...styles.flexLeft, py: 2 }}>
    <ValidatedMultipleChoice
      questionKey={questionKey}
      choices={contentBlock.choices}
      directions={contentBlock.directions}
      section={contentBlock._section}
    />
  </Box>
);

const renderUnsupported = (contentBlock) => (
  <Typography variant="h6">
    Unsupported content block type: {contentBlock.type}
  </Typography>
);

/**
 * Content dispatcher.
 *
 * Props:
 *   contentBlocks {array}   - array of content block objects
 *   keyPrefix     {string}  - stable prefix for generating questionKeys
 *                             e.g. "tutorial-tab0-step2"
 *                             Positional fallback for non-answer block anchors only.
 *   Answer blocks use permanent contentBlock.id values, unique within each lab.
 *   section       {string}  - section name passed through to answer components
 */
const Content = ({ contentBlocks, keyPrefix = '', section = '', headingLevel = 'h3', groupTitle = '', skipHeadingText = '' }) => {
  return contentBlocks
    .filter((contentBlock, index) => !(index === 0 && contentBlock.type === 'Header'
      && normalizedHeading(contentBlock.text) === normalizedHeading(skipHeadingText)))
    .map((contentBlock, index) => {
      const questionKey = contentBlock.id || (keyPrefix ? `${keyPrefix}-block${index}` : '');
      // Attach section to the contentBlock for the render functions (avoids prop drilling)
      const block = section ? { ...contentBlock, _section: section } : contentBlock;

      let rendered;
      switch (block.type) {
        case 'Header':
          rendered = renderHeader(block, headingLevel);
          break;
        case 'Text':
          rendered = renderText(block);
          break;
        case 'Image':
          rendered = renderImage(block);
          break;
        case 'Images':
          rendered = renderImages(block);
          break;
        case 'Information Table':
          rendered = renderInformationTable(block, `${groupTitle || section}: table ${index + 1}`);
          break;
        case 'Experiment Table':
          rendered = renderExperimentTable(block, `${groupTitle || section}: table ${index + 1}`);
          break;
        case 'Input':
          rendered = renderInput(block, questionKey);
          break;
        case 'Magnified Image':
          rendered = renderMagnifiedImage(block);
          break;
        case 'Image Selection':
          rendered = renderImageSelection(block, questionKey);
          break;
        case 'Multiple Choice':
          rendered = renderMultipleChoice(block, questionKey);
          break;
        default:
          rendered = renderUnsupported(block);
      }

      return (
        <React.Fragment key={contentBlock.id || index}>
          {rendered}
        </React.Fragment>
      );
    });
};

export default Content;
